import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const AlertManagementPanel = () => {
  const [alertSettings, setAlertSettings] = useState({
    critical: { enabled: true, days: 30, email: true, escalation: true },
    warning: { enabled: true, days: 60, email: true, escalation: false },
    reminder: { enabled: true, days: 90, email: false, escalation: false }
  });

  const [activeAlerts, setActiveAlerts] = useState([
    {
      id: 1,
      type: 'critical',
      title: 'Critical Warranty Expiration',
      message: '5 assets expiring within 30 days require immediate attention',
      assets: ['AT-2025-0001', 'AT-2025-0002', 'AT-2025-0005', 'AT-2025-0008', 'AT-2025-0012'],
      created: new Date(Date.now() - 2 * 60 * 60 * 1000),
      acknowledged: false,
      escalated: false
    },
    {
      id: 2,
      type: 'warning',
      title: 'Warranty Renewal Planning',
      message: '12 assets require warranty renewal planning within 60 days',
      assets: ['AT-2025-0003', 'AT-2025-0007', 'AT-2025-0009'],
      created: new Date(Date.now() - 6 * 60 * 60 * 1000),
      acknowledged: true,
      escalated: false
    },
    {
      id: 3,
      type: 'info',
      title: 'Vendor Contract Review',
      message: 'Dell Technologies contract renewal due for review',
      assets: [],
      created: new Date(Date.now() - 24 * 60 * 60 * 1000),
      acknowledged: false,
      escalated: false
    }
  ]);

  const notificationMethods = [
    { value: 'email', label: 'Email Notifications' },
    { value: 'sms', label: 'SMS Alerts' },
    { value: 'slack', label: 'Slack Integration' },
    { value: 'teams', label: 'Microsoft Teams' }
  ];

  const escalationLevels = [
    { value: 'manager', label: 'Department Manager' },
    { value: 'procurement', label: 'Procurement Team' },
    { value: 'executive', label: 'Executive Leadership' }
  ];

  const getAlertIcon = (type) => {
    switch (type) {
      case 'critical':
        return 'AlertCircle';
      case 'warning':
        return 'AlertTriangle';
      default:
        return 'Info';
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'critical':
        return 'text-error bg-error/10 border-error/20';
      case 'warning':
        return 'text-warning bg-warning/10 border-warning/20';
      default:
        return 'text-accent bg-accent/10 border-accent/20';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return `${days}d ago`;
    }
  };

  const handleAlertSettingChange = (alertType, setting, value) => {
    setAlertSettings(prev => ({
      ...prev,
      [alertType]: {
        ...prev?.[alertType],
        [setting]: value
      }
    }));
  };

  const acknowledgeAlert = (alertId) => {
    setActiveAlerts(prev =>
      prev?.map(alert =>
        alert?.id === alertId
          ? { ...alert, acknowledged: true }
          : alert
      )
    );
  };

  const escalateAlert = (alertId) => {
    setActiveAlerts(prev =>
      prev?.map(alert =>
        alert?.id === alertId
          ? { ...alert, escalated: true }
          : alert
      )
    );
  };

  const dismissAlert = (alertId) => {
    setActiveAlerts(prev => prev?.filter(alert => alert?.id !== alertId));
  };

  return (
    <div className="space-y-6">
      {/* Alert Configuration */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
            <Icon name="Settings" size={20} className="text-warning" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Alert Configuration</h3>
            <p className="text-sm text-muted-foreground">Configure warranty expiration alert thresholds</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {Object.entries(alertSettings)?.map(([type, settings]) => (
            <div key={type} className="space-y-4 p-4 border border-border rounded-lg">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-card-foreground capitalize">{type} Alerts</h4>
                <Checkbox
                  checked={settings?.enabled}
                  onChange={(e) => handleAlertSettingChange(type, 'enabled', e?.target?.checked)}
                />
              </div>

              {settings?.enabled && (
                <div className="space-y-3">
                  <Input
                    label="Days Before Expiration"
                    type="number"
                    value={settings?.days}
                    onChange={(e) => handleAlertSettingChange(type, 'days', parseInt(e?.target?.value))}
                    min="1"
                    max="365"
                  />

                  <div className="space-y-2">
                    <Checkbox
                      label="Email Notifications"
                      checked={settings?.email}
                      onChange={(e) => handleAlertSettingChange(type, 'email', e?.target?.checked)}
                    />
                    <Checkbox
                      label="Auto Escalation"
                      checked={settings?.escalation}
                      onChange={(e) => handleAlertSettingChange(type, 'escalation', e?.target?.checked)}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <Button variant="default" iconName="Save" iconPosition="left">
            Save Configuration
          </Button>
        </div>
      </div>
      {/* Active Alerts */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
              <Icon name="Bell" size={20} className="text-error" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-card-foreground">Active Alerts</h3>
              <p className="text-sm text-muted-foreground">Current warranty expiration alerts</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left">
              Refresh
            </Button>
            <Button variant="outline" size="sm" iconName="Settings" iconPosition="left">
              Manage
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {activeAlerts?.map((alert) => (
            <div
              key={alert?.id}
              className={`border rounded-lg p-4 ${getAlertColor(alert?.type)} ${
                alert?.acknowledged ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <Icon name={getAlertIcon(alert?.type)} size={20} />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-medium">{alert?.title}</h4>
                      {alert?.escalated && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-error text-error-foreground">
                          Escalated
                        </span>
                      )}
                      {alert?.acknowledged && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success text-success-foreground">
                          Acknowledged
                        </span>
                      )}
                    </div>
                    <p className="text-sm mb-2">{alert?.message}</p>
                    {alert?.assets?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {alert?.assets?.slice(0, 3)?.map((assetId) => (
                          <span
                            key={assetId}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-background text-foreground"
                          >
                            {assetId}
                          </span>
                        ))}
                        {alert?.assets?.length > 3 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-background text-foreground">
                            +{alert?.assets?.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                    <p className="text-xs opacity-75">{formatTimestamp(alert?.created)}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {!alert?.acknowledged && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => acknowledgeAlert(alert?.id)}
                      iconName="Check"
                      iconPosition="left"
                    >
                      Acknowledge
                    </Button>
                  )}
                  {!alert?.escalated && alert?.type === 'critical' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => escalateAlert(alert?.id)}
                      iconName="ArrowUp"
                      iconPosition="left"
                    >
                      Escalate
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => dismissAlert(alert?.id)}
                    iconName="X"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {activeAlerts?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
            <h4 className="text-lg font-medium text-card-foreground mb-2">No Active Alerts</h4>
            <p className="text-muted-foreground">All warranty expirations are under control</p>
          </div>
        )}
      </div>
      {/* Notification Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Mail" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Notification Preferences</h3>
            <p className="text-sm text-muted-foreground">Configure how you receive warranty alerts</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <Select
              label="Primary Notification Method"
              options={notificationMethods}
              value="email"
              onChange={() => {}}
              className="mb-4"
            />
            <Select
              label="Escalation Level"
              options={escalationLevels}
              value="manager"
              onChange={() => {}}
            />
          </div>
          <div className="space-y-3">
            <Checkbox label="Daily digest emails" checked />
            <Checkbox label="Weekend notifications" />
            <Checkbox label="Mobile push notifications" checked />
            <Checkbox label="Slack channel integration" checked />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button variant="outline" iconName="Save" iconPosition="left">
            Update Preferences
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AlertManagementPanel;